import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import {
  containerArriveAtFacilityAtom,
  containerDepartFacilityAtom,
  containerLoadAtom,
  containerPickupAtom,
  containerReturnToPortAtom,
  containerRollAtom,
  containerShipAtom,
  containerUnloadAtom,
  containerVoidAtom,
} from '../_graphql/mutationContainerTransitions';
import { containerDeleteAtom } from '../_graphql/mutationContainerUpdate';
import { containersListQueryAtom } from '../_graphql/queryContainersList';
import { containerStatusCountsQueryAtom } from '../_graphql/queryContainerStatusCounts';
import {
  ContainerRecord,
  ContainerSortOption,
  ContainerStatus,
  enumContainerSortOption,
} from '../types/container';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedBooking = atomFromQueryString.string('booking');
export const $selectedFacility = atomFromQueryString.string('facility');
export const $selectedContainerType =
  atomFromQueryString.string('containerType');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

// 🟣🟣🟣 STATUS FILTER
export const $selectedStatus = atomFromQueryString.string<
  ContainerStatus | 'all'
>('status', 'all');
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<ContainerStatus>('colStatuses');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $selectedContainer = atom<ContainerRecord | null>(null);
export const $isVoidDialogOpen = atom<boolean>(false);
export const $isTransitionDialogOpen = atom<boolean>(false);
export const $isRollDialogOpen = atom<boolean>(false);
export const $currentSorting = atom<ContainerSortOption[]>([
  enumContainerSortOption.CreatedAtDesc,
]);

// 🟢🟢🟢 REMOTE DATA
export const $remoteContainers = containersListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedBooking = get($selectedBooking);
  const selectedFacility = get($selectedFacility);
  const selectedContainerType = get($selectedContainerType);
  const selectedStatus = get($selectedStatus);
  const columnSelectedStatuses = get($columnSelectedStatuses);
  const limit = get($limit);
  const offset = get($offset);
  const sortingParams = get($currentSorting);

  const params: {
    searchTerm?: string;
    bookingIds?: string[];
    facilityIds?: string[];
    containerTypeIds?: string[];
    status?: ContainerStatus[];
    limit: number;
    offset: number;
    sorting: ContainerSortOption[];
  } = {
    limit,
    offset,
    sorting: sortingParams,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (selectedBooking && selectedBooking !== 'all') {
    params.bookingIds = [selectedBooking];
  }
  if (selectedFacility && selectedFacility !== 'all') {
    params.facilityIds = [selectedFacility];
  }
  if (selectedContainerType && selectedContainerType !== 'all') {
    params.containerTypeIds = [selectedContainerType];
  }

  if (columnSelectedStatuses.size > 0) {
    params.status = Array.from(columnSelectedStatuses);
  } else if (selectedStatus && selectedStatus !== 'all') {
    params.status = [selectedStatus];
  }

  return {
    variables: params,
  };
});

// 🟢🟢🟢 STATUS COUNTS
export const $containerStatusCounts = containerStatusCountsQueryAtom((get) => {
  const selectedBooking = get($selectedBooking);
  const selectedFacility = get($selectedFacility);
  const selectedContainerType = get($selectedContainerType);
  const searchTerm = get($searchTerm);

  const params: {
    searchTerm?: string;
    bookingIds?: string[];
    facilityIds?: string[];
    containerTypeIds?: string[];
  } = {};

  if (searchTerm) params.searchTerm = searchTerm;
  if (selectedBooking && selectedBooking !== 'all') {
    params.bookingIds = [selectedBooking];
  }
  if (selectedFacility && selectedFacility !== 'all') {
    params.facilityIds = [selectedFacility];
  }
  if (selectedContainerType && selectedContainerType !== 'all') {
    params.containerTypeIds = [selectedContainerType];
  }

  return {
    variables: params,
  };
});

// 🟠🟠🟠 ACTIONS
export const $deleteContainer = containerDeleteAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $voidContainer = containerVoidAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $pickupContainer = containerPickupAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $arriveContainerAtFacility = containerArriveAtFacilityAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $loadContainer = containerLoadAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $departContainerFacility = containerDepartFacilityAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $returnContainerToPort = containerReturnToPortAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $shipContainer = containerShipAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $unloadContainer = containerUnloadAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});

export const $rollContainer = containerRollAtom({
  onSuccess: (get) => {
    get($remoteContainers).refetch?.();
    get($containerStatusCounts).refetch?.();
  },
});
